import { useMemo } from 'react';
import { ROLE } from '@/common/role';
import type { User } from '@/common/user';
import { useAppContext } from '@/context/AppContext';
import { useAssetReadOnly } from '@/context/AssetReadOnlyContext';
import { useKeycloak } from '@/context/KeycloakContext';

const SUPER_ADMIN_ROLES: ROLE[] = [
	ROLE.ORGANIZATION_READ,
	ROLE.ORGANIZATION_CREATE,
	ROLE.ORGANIZATION_UPDATE,
	ROLE.ORGANIZATION_DELETE,
];

// Stable reference: `profile?.organizations || []` would mint a new array on every
// call, which breaks any consumer that depends on it by reference (e.g. a
// useEffect dependency array) by making that dependency look "changed" every render.
const EMPTY_ORGANIZATIONS: User['organizations'] = [];

// Roles that modify organization-scoped data. They are withheld while viewing an asset of an inactive
// organization. User and organization administration stays available (an org must remain reactivatable).
const ORG_SCOPED_WRITE_ROLES: ReadonlySet<ROLE> = new Set(
	Object.values(ROLE).filter(
		(role) =>
			(/_(CREATE|UPDATE|DELETE)$/.test(role) || role === ROLE.FILES_UPLOAD) &&
			!role.startsWith('USERS_') &&
			!role.startsWith('ORGANIZATION_'),
	),
);

/**
 * The current user's roles, minus the write roles when the current asset is read-only
 * (its organization is inactive). All permission checks go through this.
 */
export function useEffectiveRoles(): Set<ROLE> {
	const { roles } = useKeycloak();
	const { isReadOnly } = useAssetReadOnly();
	return useMemo(
		() => (isReadOnly ? new Set([...roles].filter((role) => !ORG_SCOPED_WRITE_ROLES.has(role))) : roles),
		[roles, isReadOnly],
	);
}

/**
 * Custom hook to check if the current user has the specified permissions
 * @param permissions A single permission or an array of permissions to check against the user's roles
 * @returns true if the user has all the specified permissions, false otherwise
 */
export function usePermission(permissions: ROLE | ROLE[]): boolean {
	const roles = useEffectiveRoles();
	return hasPermission(roles, permissions);
}

/**
 * Custom hook to check if the current user has at least one of the specified permissions
 * @param permissions A single permission or an array of permissions to check against the user's roles
 * @returns true if the user has at least one of the specified permissions, false otherwise
 */
export function useAnyPermission(permissions: ROLE | ROLE[]): boolean {
	const roles = useEffectiveRoles();
	return hasAnyPermission(roles, permissions);
}

/**
 * Helper functions to get pre-bound permission checkers for easier usage in components
 * This allows components to call hasPermission and hasAnyPermission without needing to access roles directly
 * return functions that are already bound to the current user's roles
 */
export function usePermissionChecker() {
	const roles = useEffectiveRoles();

	// Return functions that are pre-bound to the current user's roles for easier usage in components
	const hasPermissionFn = hasPermission.bind(null, roles);

	// This function checks if the user has at least one of the specified permissions
	const hasAnyPermissionFn = hasAnyPermission.bind(null, roles);

	return { hasPermission: hasPermissionFn, hasAnyPermission: hasAnyPermissionFn };
}

/**
 * Custom hook to get permission metadata such as whether the user is a super admin and their associated organizations
 * This can be used in components to conditionally render UI elements based on the user's permissions and organizational context
 * It combines information from both Keycloak roles and the user's profile from the app context
 * @returns An object containing isSuperAdmin boolean and an array of organizations the user belongs to
 * isSuperAdmin is determined by checking if the user has all the roles defined in SUPER_ADMIN_ROLES
 * organizations are retrieved from the user's profile in the app context, which should have been populated on login
 */
export function usePermissionMetadata() {
	const { roles } = useKeycloak();
	const { profile } = useAppContext();
	const isSuperAdmin = SUPER_ADMIN_ROLES.every((role) => roles.has(role));
	return { isSuperAdmin, organizations: profile?.organizations || EMPTY_ORGANIZATIONS };
}

/**
 * True when every permission in `permissions` is held. An empty list is denied:
 * `[].every(...)` is vacuously true, which would grant access to anyone.
 */
export function hasPermission(roles: Set<ROLE>, permissions: ROLE | ROLE[]): boolean {
	const requiredPermissionsArray = Array.isArray(permissions) ? permissions : [permissions];
	return requiredPermissionsArray.length > 0 && requiredPermissionsArray.every((permission) => roles.has(permission));
}

/** True when at least one permission in `permissions` is held. An empty list is denied. */
export function hasAnyPermission(roles: Set<ROLE>, permissions: ROLE | ROLE[]): boolean {
	const requiredPermissionsArray = Array.isArray(permissions) ? permissions : [permissions];
	return requiredPermissionsArray.some((permission) => roles.has(permission));
}
